/**
 * Coffee Bean Clicker v2 - Sound Manager
 * Web Audio API based procedural sound synthesis.
 * No audio files needed -- all sounds are generated programmatically.
 */

type SfxType =
  | 'tap'
  | 'crit'
  | 'combo'
  | 'buy'
  | 'prestige'
  | 'achievement'
  | 'roastTick'
  | 'roastPerfect';

let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let enabled = true;

// ===================================================
//  Init / Resume
// ===================================================

function ensureContext(): boolean {
  if (audioCtx) return true;
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    audioCtx = new AC();
    masterGain = audioCtx.createGain();
    masterGain.gain.value = 0.5;
    masterGain.connect(audioCtx.destination);
    return true;
  } catch {
    return false;
  }
}

function resumeCtx(): void {
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
}

// ===================================================
//  Low-level helpers
// ===================================================

function playTone(
  freq: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume = 0.12,
  startDelay = 0,
): void {
  if (!audioCtx || !masterGain || !enabled) return;
  resumeCtx();

  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.connect(gain);
  gain.connect(masterGain);

  osc.type = type;
  const t = audioCtx.currentTime + startDelay;
  osc.frequency.setValueAtTime(freq, t);
  gain.gain.setValueAtTime(volume, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  osc.start(t);
  osc.stop(t + duration);
}

function playNoise(duration: number, volume = 0.05, startDelay = 0): void {
  if (!audioCtx || !masterGain || !enabled) return;
  resumeCtx();

  const bufferSize = audioCtx.sampleRate * duration;
  const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  const src = audioCtx.createBufferSource();
  src.buffer = buffer;

  const gain = audioCtx.createGain();
  const t = audioCtx.currentTime + startDelay;
  gain.gain.setValueAtTime(volume, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

  // Bandpass filter for "ching" quality
  const filter = audioCtx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 3000;
  filter.Q.value = 2;

  src.connect(filter);
  filter.connect(gain);
  gain.connect(masterGain);

  src.start(t);
  src.stop(t + duration);
}

// ===================================================
//  SFX Definitions
// ===================================================

/** Short "pok" tap sound */
function playTap(): void {
  playTone(600, 0.06, 'sine', 0.1);
  playTone(300, 0.04, 'triangle', 0.05, 0.01);
}

/** Higher pitch "ding!" critical hit */
function playCrit(): void {
  playTone(1200, 0.1, 'sine', 0.15);
  playTone(1800, 0.15, 'sine', 0.1, 0.05);
}

/** Rising tone combo - pitch scales with combo level */
function playCombo(comboLevel: number = 1): void {
  const baseFreq = 400 + Math.min(comboLevel, 50) * 15;
  playTone(baseFreq, 0.08, 'sine', 0.1);
  playTone(baseFreq * 1.25, 0.08, 'sine', 0.08, 0.04);
  playTone(baseFreq * 1.5, 0.1, 'sine', 0.06, 0.08);
}

/** "Cha-ring" purchase sound */
function playBuy(): void {
  playTone(880, 0.06, 'sine', 0.1);
  playTone(1320, 0.08, 'triangle', 0.08, 0.04);
  playNoise(0.06, 0.03, 0.02);
}

/** Rising harp glissando for prestige */
function playPrestige(): void {
  const notes = [523, 659, 784, 988, 1175, 1397, 1568, 2093]; // C5 to C7 scale
  notes.forEach((freq, i) => {
    playTone(freq, 0.2 - i * 0.01, 'sine', 0.1 - i * 0.005, i * 0.06);
  });
  // Shimmer at the end
  playNoise(0.3, 0.04, notes.length * 0.06);
}

/** Fanfare for achievement */
function playAchievement(): void {
  // Triumphant chord: C-E-G-C
  playTone(523, 0.15, 'sine', 0.1, 0);       // C5
  playTone(659, 0.15, 'sine', 0.1, 0.08);     // E5
  playTone(784, 0.15, 'sine', 0.1, 0.16);     // G5
  playTone(1047, 0.25, 'sine', 0.12, 0.24);   // C6

  // Higher octave stinger
  playTone(1568, 0.1, 'triangle', 0.06, 0.36);
  playTone(2093, 0.15, 'sine', 0.05, 0.4);
}

/** Ticking sound for roasting gauge */
function playRoastTick(): void {
  playTone(800, 0.03, 'square', 0.04);
  playTone(600, 0.02, 'square', 0.03, 0.02);
}

/** Perfect roast celebration */
function playRoastPerfect(): void {
  playTone(880, 0.1, 'sine', 0.12, 0);
  playTone(1100, 0.1, 'sine', 0.12, 0.06);
  playTone(1320, 0.1, 'sine', 0.12, 0.12);
  playTone(1760, 0.2, 'sine', 0.15, 0.18);
  playNoise(0.15, 0.04, 0.25);
}

// ===================================================
//  Public API (Singleton)
// ===================================================

/** Extra parameter for combo level */
let _comboLevel = 1;

export const SoundManager = {
  /** Initialize audio context and register interaction listeners */
  init(): void {
    ensureContext();

    const unlockAudio = () => {
      ensureContext();
      resumeCtx();
    };

    ['touchstart', 'touchend', 'click', 'pointerdown'].forEach((evt) => {
      document.addEventListener(evt, unlockAudio, { once: true, passive: true });
    });

    // Background handling
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Suspend context when app goes to background
        audioCtx?.suspend();
      } else {
        resumeCtx();
      }
    });
  },

  /** Play a sound effect */
  play(type: SfxType): void {
    if (!enabled) return;
    if (!ensureContext()) return;

    switch (type) {
      case 'tap':         playTap(); break;
      case 'crit':        playCrit(); break;
      case 'combo':       playCombo(_comboLevel); break;
      case 'buy':         playBuy(); break;
      case 'prestige':    playPrestige(); break;
      case 'achievement': playAchievement(); break;
      case 'roastTick':   playRoastTick(); break;
      case 'roastPerfect': playRoastPerfect(); break;
    }
  },

  /** Set the current combo level (affects combo sound pitch) */
  setComboLevel(level: number): void {
    _comboLevel = level;
  },

  /** Enable or disable all sound effects */
  setEnabled(value: boolean): void {
    enabled = value;
  },

  /** Check if sound is enabled */
  isEnabled(): boolean {
    return enabled;
  },

  /** Toggle sound on/off */
  toggle(): boolean {
    enabled = !enabled;
    return enabled;
  },

  /** Set master volume (0.0 ~ 1.0) */
  setVolume(vol: number): void {
    if (masterGain) {
      masterGain.gain.value = Math.max(0, Math.min(1, vol));
    }
  },

  /** Dispose audio context */
  dispose(): void {
    if (audioCtx) {
      audioCtx.close();
      audioCtx = null;
      masterGain = null;
    }
  },
};
